/* ════════════════════════════════════════════════════════════════
   SETIECA — Motor de firma de correo

   ⚠ **Portado tal cual desde `setieca_procesos/js/firma.js`, y sigue
   siendo JavaScript a propósito.** Son 1.100 líneas de códec de GIF
   —decodificador y codificador propios, sin librerías— más el dibujo
   en canvas: código que indexa arrays en cada línea. Bajo el
   `noUncheckedIndexedAccess` de este proyecto habría que sembrarlo de
   `!` o de guardas, y eso es mucha rotación sobre código que YA
   funciona en producción, con riesgo real de cambiarle el
   comportamiento sin querer. La seguridad de tipos que importa es la
   del borde, y ésa la da `firma.d.ts`: quien lo llama desde TypeScript
   lo ve completamente tipado.

   ⚠ **Si hay que tocar el dibujo, se toca AQUÍ y se replica allá** —o
   al revés—: son dos copias del mismo motor y no hay nada que las
   mantenga sincronizadas.

   Lo original decía:

   Dibuja la tarjeta en canvas y exporta PNG / GIF animado.
   · Los logos se cargan desde /img (mismo origen) y si faltan se
     dibuja un respaldo vectorial — nunca truena por ERR_FILE_NOT_FOUND.
   · Los datos vienen de la base de datos, no de la URL.
   · El texto se auto-ajusta cuando el nombre o correo es muy largo.
   ════════════════════════════════════════════════════════════════ */

/* ── Lienzo maestro (2x) ─────────────────────────────────────── */
const W = 1374,
  H = 462
const SANS = "Montserrat, 'Segoe UI', Helvetica, Arial, sans-serif"
const SCRIPT_F = "Sacramento, 'Segoe Script', 'Brush Script MT', cursive"
const REG = { x: 24, y: 50, w: 332, h: 304 } // zona animada (logo)

/* ── Guion de tiempos (ms) ───────────────────────────────────── */
const T = {
  entra25: [0, 520],
  palabra: { ini: 560, paso: 80, dur: 280 },
  frase: { ini: 1280, paso: 30, dur: 200 },
  salida: [3030, 3480],
  logo: 3530,
  trazo: 1200,
  cierre: [6100, 6500],
  total: 6600,
}

/* Pesos de Montserrat usados en la tarjeta. Todos se precargan en
   preparar(): si no se pide un peso concreto, el navegador lo simula
   engordando el trazo y el resultado se ve sucio. */
const PESO = { medio: 500, semi: 600, bold: 700 }

const PALABRA = "Años",
  FRASE = "Evolucionando la Logística",
  FS_FRASE = 13

/* Copy de marca del panel derecho */
const PILARES = [
  ["EXPERIENCIA", 73, ["25 años de trayectoria", "evolucionando la logística."]],
  ["COMPROMISO", 170, ["Con nuestros clientes", "y con Venezuela."]],
  ["SOLUCIONES", 267, ["Logísticas integrales,", "confiables y eficientes."]],
]

/* ── Formas fijas de la tarjeta (sin texto: el texto va en canvas
      para poder usar las fuentes de la página) ────────────────── */
const FORMAS = `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">
<defs>
<linearGradient id="panel" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#AE5B75"/><stop offset="0.45" stop-color="#9A3E5C"/><stop offset="1" stop-color="#88304E"/></linearGradient>
<linearGradient id="band" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stop-color="#3A0E22"/><stop offset="0.5" stop-color="#6B1D3E"/><stop offset="1" stop-color="#8C3553"/></linearGradient>
<linearGradient id="gold" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#F2DDA6"/><stop offset="0.35" stop-color="#C99A43"/><stop offset="0.62" stop-color="#F3E2B4"/><stop offset="1" stop-color="#B8862F"/></linearGradient>
<filter id="sombra" x="-6%" y="-8%" width="112%" height="118%"><feDropShadow dx="0" dy="3" stdDeviation="4" flood-color="#3A0E22" flood-opacity="0.22"/></filter>
<clipPath id="cardClip"><rect x="18" y="12" width="1344" height="434" rx="28"/></clipPath>
</defs>
<rect width="${W}" height="${H}" fill="#FFFFFF"/>
<g filter="url(#sombra)"><rect x="18" y="12" width="1344" height="434" rx="28" fill="#FFFFFF"/></g>
<g clip-path="url(#cardClip)">
  <path d="M930,12 C916,70 896,110 896,156 C896,215 920,240 920,292 C920,335 884,352 870,372 H1362 V40 A28,28 0 0 0 1334,12 Z" fill="url(#panel)"/>
  <path d="M906,12 C892,70 874,110 874,156 C874,215 898,240 898,292 C898,335 860,352 846,372" fill="none" stroke="url(#gold)" stroke-width="3"/>
  <path d="M18,372 H1362 V418 A28,28 0 0 1 1334,446 H46 A28,28 0 0 1 18,418 Z" fill="url(#band)"/>
</g>
<line x1="366" y1="56" x2="366" y2="334" stroke="#CFAA5E" stroke-width="1.6"/>
<!-- La línea bajo el cargo se dibuja en canvas: su largo depende del texto -->
<g fill="#8E2049"><circle cx="433" cy="149" r="13.5"/><circle cx="433" cy="193" r="13.5"/><circle cx="433" cy="237" r="13.5"/><circle cx="433" cy="281" r="13.5"/></g>
<g fill="#FFFFFF">
  <g transform="translate(425.5,141.5) scale(0.63)"><path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/></g>
  <g transform="translate(425,186) scale(0.66)"><path d="M2.5 6.2c0-1 .8-1.7 1.7-1.7h15.6c1 0 1.7.8 1.7 1.7v11.6c0 1-.8 1.7-1.7 1.7H4.2c-1 0-1.7-.8-1.7-1.7V6.2zm2.4.3 7.1 4.6 7.1-4.6H4.9zm14.7 2.1-6.9 4.5c-.5.3-1.1.3-1.6 0L4.2 8.6v9h15.4v-9z"/></g>
  <g transform="translate(425.5,229.5) scale(0.63)" fill="none" stroke="#FFFFFF" stroke-width="1.7"><circle cx="12" cy="12" r="9.2"/><ellipse cx="12" cy="12" rx="4.1" ry="9.2"/><line x1="2.8" y1="12" x2="21.2" y2="12"/></g>
  <g transform="translate(426,273.5) scale(0.63)"><path d="M12 2.2c-3.9 0-7 3.1-7 7 0 5.1 7 12.6 7 12.6s7-7.5 7-12.6c0-3.9-3.1-7-7-7zm0 9.6a2.6 2.6 0 1 1 0-5.2 2.6 2.6 0 0 1 0 5.2z"/></g>
</g>
<g fill="none" stroke="#E7B966" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round">
  <g transform="translate(988,58) scale(1.32)"><circle cx="24" cy="24" r="21"/><ellipse cx="24" cy="24" rx="9.5" ry="21"/><line x1="3" y1="24" x2="45" y2="24"/><path d="M6.5 13.5h35M6.5 34.5h35"/></g>
  <g transform="translate(988,156) scale(2.64)" stroke-width="0.95"><path d="M3 4h8"/><path d="M3 3 2 14l6.5 6.5a1 1 0 1 0 3-3"/><path d="m11 17 2 2a1 1 0 1 0 3-3"/><path d="m14 14 2.5 2.5a1 1 0 1 0 3-3l-3.88-3.88a3 3 0 0 0-4.24 0l-.88.88a1 1 0 1 1-3-3l2.81-2.81a5.79 5.79 0 0 1 7.06-.87l.47.28a2 2 0 0 0 1.42.25L21 4"/><path d="m21 3 1 11h-2"/></g>
  <g transform="translate(990.6,238) scale(1.32)"><path d="M23,4 L42,12 L23,20 L4,12 Z"/><path d="M4,12 V32 L23,40 V20"/><path d="M42,12 V32 L23,40"/><path d="M14,8 L33,16 V25"/></g>
</g>
</svg>`

/* ── Estado del módulo ───────────────────────────────────────── */
let formasImg = null // <img> con las formas fijas
let baseCv = null // canvas con la tarjeta completa (sin zona animada)
let logoFull = null // { el, w, h }  logo completo
let marca25 = null // { el, w, h }  marca "25"
let swapCv = null // canvas reutilizable para el barrido
let listo = false

/* Animación propia del cliente: un GIF que ya trae toda la secuencia.
   Cuando existe, sustituye por completo a la animación sintética.
   { frames:[{img,delay,acum}], total, w, h, rec:{x,y,w,h}, idxFinal } */
let animLogo = null

let fsPalabra = 47,
  anchoFrase = 0
let datos = { nombre: "Nombre Apellido", cargo: "Cargo / Posición", telefono: "", email: "" }
let empresa = {}

/* Escala del logo dentro de su zona, en % (ajustable desde la interfaz) */
let escalaLogo = 100

const DEF = {
  emp_logo_escala: "100",
  emp_rif: "J-30818222-2",
  emp_telefono: "+58 (241) 823.35.60 / 823.15.58",
  emp_web: "www.setieca.com.ve",
  emp_dir1: "Sector Hacienda La Salina,",
  emp_dir2: "Galpón Nº 8. Puerto Cabello,",
  emp_dir3: "Estado Carabobo, Venezuela.",
  emp_slogan: "25 años de confianza, compromiso y crecimiento junto a nuestros clientes.",
}
const cfg = (k) => (empresa[k] && String(empresa[k]).trim()) || DEF[k] || ""

/* ── Utilidades ──────────────────────────────────────────────── */
const cargarImg = (src) =>
  new Promise((ok, err) => {
    const i = new Image()
    i.onload = () => ok(i)
    i.onerror = () => err(new Error(`No se pudo cargar ${src}`))
    i.src = src
  })
const svgAImg = (s) => cargarImg(`data:image/svg+xml;charset=utf-8,${encodeURIComponent(s)}`)
const salidaEase = (t) => 1 - (1 - t) ** 3
const clamp01 = (t) => (t < 0 ? 0 : t > 1 ? 1 : t)
const tramo = (t, a, b) => clamp01((t - a) / (b - a))
const esperar = () => new Promise((r) => requestAnimationFrame(() => setTimeout(r, 0)))

function texto(ctx, txt, x, y, o) {
  ctx.save()
  ctx.font = `${o.weight || 400} ${o.size}px ${o.familia || SANS}`
  ctx.fillStyle = o.color
  ctx.textAlign = o.align || "left"
  ctx.textBaseline = o.base || "alphabetic"
  if (o.ls) ctx.letterSpacing = `${o.ls}px`
  if (o.alpha !== undefined) ctx.globalAlpha = o.alpha
  ctx.fillText(txt, x, y)
  ctx.restore()
}

/* Reduce el tamaño de fuente hasta que el texto quepa en maxW.
   Devuelve el ancho real dibujado (lo usa la línea bajo el cargo). */
function textoAjustado(ctx, txt, x, y, o, maxW) {
  let size = o.size,
    ancho = 0
  ctx.save()
  while (size > 9) {
    ctx.font = `${o.weight || 400} ${size}px ${o.familia || SANS}`
    ancho = ctx.measureText(txt).width
    if (ancho <= maxW) break
    size -= 0.5
  }
  ctx.restore()
  texto(ctx, txt, x, y, Object.assign({}, o, { size }))
  return ancho
}

const anchoTexto = (ctx, txt, size, weight) => {
  ctx.save()
  ctx.font = `${weight} ${size}px ${SANS}`
  let w = 0
  for (const ch of txt) w += ctx.measureText(ch).width
  ctx.restore()
  return w
}

/* ── Respaldos vectoriales (si aún no se subieron los PNG) ────── */
function crearLogoFallback() {
  const c = document.createElement("canvas")
  c.width = 388
  c.height = 322
  const x = c.getContext("2d")

  const g = x.createLinearGradient(60, 20, 330, 190)
  g.addColorStop(0, "#D9A7B8")
  g.addColorStop(0.5, "#A94A6B")
  g.addColorStop(1, "#7A1B3E")

  x.save()
  x.font = `700 172px Georgia, 'Times New Roman', serif`
  x.fillStyle = g
  x.textAlign = "center"
  x.textBaseline = "alphabetic"
  x.fillText("25", 194, 158)
  x.restore()

  texto(x, "setieca", 194, 240, {
    size: 66,
    weight: 600,
    color: "#8E2049",
    align: "center",
    ls: -1.5,
  })
  texto(x, cfg("emp_sub") || "Agentes Aduanales, c.a.", 194, 270, {
    size: 17,
    color: "#B08A96",
    align: "center",
  })
  texto(x, `RIF: ${cfg("emp_rif")}`, 194, 300, {
    size: 16,
    weight: 700,
    color: "#6E1435",
    align: "center",
  })

  return { el: c, w: c.width, h: c.height }
}

function crearMarca25Fallback() {
  const c = document.createElement("canvas")
  c.width = 150
  c.height = 110
  const x = c.getContext("2d")
  const g = x.createLinearGradient(10, 5, 140, 100)
  g.addColorStop(0, "#D9A7B8")
  g.addColorStop(0.5, "#A94A6B")
  g.addColorStop(1, "#7A1B3E")
  x.font = `700 104px Georgia, 'Times New Roman', serif`
  x.fillStyle = g
  x.textAlign = "center"
  x.textBaseline = "middle"
  x.fillText("25", 75, 58)
  return { el: c, w: c.width, h: c.height }
}

/* ── Tarjeta base (todo menos la zona animada del logo) ───────── */
function dibujarBase(ctx) {
  ctx.clearRect(0, 0, W, H)
  ctx.drawImage(formasImg, 0, 0)

  /* Jerarquía tipográfica — Montserrat en todo el bloque de datos:
       Bold 700     nombre y titulares de los pilares
       SemiBold 600 cargo
       Medium 500   datos de contacto y cuerpo de los pilares          */
  /* El nombre no va en negro puro: lleva matiz borgoña de la marca */
  textoAjustado(
    ctx,
    datos.nombre || "Nombre Apellido",
    423,
    66,
    { size: 28, weight: PESO.bold, color: "#3D1526" },
    440,
  )
  const cargoTxt = datos.cargo || "Cargo / Posición"
  const cargoW = textoAjustado(
    ctx,
    cargoTxt,
    423,
    95,
    { size: 18, weight: PESO.semi, color: "#9C6A33" },
    440,
  )

  // Línea dorada exactamente del largo del cargo
  if (cargoTxt) {
    ctx.save()
    ctx.strokeStyle = "#CFAA5E"
    ctx.lineWidth = 1.6
    ctx.beginPath()
    ctx.moveTo(423, 105.5)
    ctx.lineTo(423 + cargoW, 105.5)
    ctx.stroke()
    ctx.restore()
  }

  // Teléfono propio del trabajador; si no tiene, el de la central
  const tel = datos.telefono?.trim() || cfg("emp_telefono")

  const lineas = [
    [tel, 155],
    [datos.email || "", 199],
    [cfg("emp_web"), 243],
    [cfg("emp_dir1"), 287],
    [cfg("emp_dir2"), 312],
    [cfg("emp_dir3"), 337],
  ]
  lineas.forEach(([t, y]) => {
    if (t) textoAjustado(ctx, t, 466, y, { size: 17.5, weight: PESO.medio, color: "#241F21" }, 400)
  })

  PILARES.forEach(([titulo, y, cuerpo]) => {
    texto(ctx, titulo, 1086, y, { size: 17.5, weight: PESO.bold, color: "#E9BE72", ls: 0.7 })
    cuerpo.forEach((l, i) => {
      texto(ctx, l, 1086, y + 28 + i * 26, { size: 16.5, weight: PESO.medio, color: "#F7EBEF" })
    })
  })

  textoAjustado(
    ctx,
    cfg("emp_slogan"),
    672,
    420,
    { size: 38, color: "#E2B96C", align: "center", familia: SCRIPT_F },
    1180,
  )
}

/* ── Barrido de aparición del logo ───────────────────────────── */
function dibujarLogoBarrido(ctx, avance, alpha) {
  const o = swapCv.getContext("2d")
  o.clearRect(0, 0, W, H)
  o.save()
  o.beginPath()
  o.rect(64, 54, 280, 296)
  o.clip()
  o.drawImage(logoFull.el, 21.5, 33.7, 387.9, 321.4)
  o.restore()

  if (avance < 1) {
    const g = o.createLinearGradient(245, 386, 150, 24)
    const a = Math.max(0, avance - 0.05)
    g.addColorStop(0, "rgba(255,255,255,1)")
    if (a > 0) g.addColorStop(a, "rgba(255,255,255,1)")
    g.addColorStop(Math.min(1, avance), "rgba(255,255,255,0)")
    g.addColorStop(1, "rgba(255,255,255,0)")
    o.globalCompositeOperation = "destination-in"
    o.fillStyle = g
    o.fillRect(0, 0, W, H)
    o.globalCompositeOperation = "source-over"
  }

  ctx.save()
  ctx.globalAlpha = alpha
  ctx.drawImage(swapCv, 0, 0)
  ctx.restore()
}

/* ── Secuencia "25 Años · Evolucionando la Logística" ─────────── */
function dibujarFrase(ctx, t, alpha) {
  const altoImg = 86
  const anchoImg = altoImg * (marca25.w / marca25.h)
  const filaW = anchoImg + 12 + anchoFrase
  const x0 = REG.x + 2 + (326 - filaW) / 2
  const cy = 203

  const e25 = salidaEase(tramo(t, T.entra25[0], T.entra25[1]))
  ctx.save()
  ctx.globalAlpha = alpha * e25
  const esc = 0.92 + 0.08 * e25
  const cx = x0 + anchoImg / 2
  ctx.translate(cx, cy)
  ctx.scale(esc, esc)
  ctx.translate(-cx, -cy)
  ctx.drawImage(marca25.el, x0, cy - altoImg / 2, anchoImg, altoImg)
  ctx.restore()

  const altoCol = fsPalabra * 0.84 + 3 + FS_FRASE * 1.1
  const topCol = cy - altoCol / 2
  const xCol = x0 + anchoImg + 12

  ctx.save()
  ctx.textBaseline = "top"

  let x = xCol
  ctx.font = `600 ${fsPalabra}px ${SANS}`
  ;[...PALABRA].forEach((ch, i) => {
    const p = salidaEase(
      tramo(
        t,
        T.palabra.ini + i * T.palabra.paso,
        T.palabra.ini + i * T.palabra.paso + T.palabra.dur,
      ),
    )
    ctx.globalAlpha = alpha * p
    ctx.fillStyle = "#6E1435"
    ctx.fillText(ch, x, topCol + (1 - p) * 10 - fsPalabra * 0.1)
    x += ctx.measureText(ch).width
  })

  x = xCol
  ctx.font = `400 ${FS_FRASE}px ${SANS}`
  const yFrase = topCol + fsPalabra * 0.84 + 3
  ;[...FRASE].forEach((ch, i) => {
    const p = salidaEase(
      tramo(t, T.frase.ini + i * T.frase.paso, T.frase.ini + i * T.frase.paso + T.frase.dur),
    )
    ctx.globalAlpha = alpha * p
    ctx.fillStyle = "#8E2049"
    ctx.fillText(ch, x, yFrase + (1 - p) * 6)
    x += ctx.measureText(ch).width
  })
  ctx.restore()
}

/* Duración del bucle: la del GIF del cliente si lo hay, si no la sintética */
function duracion() {
  return animLogo ? animLogo.total : T.total
}

/* Instante que representa el estado "final" (logo completo) */
function tFinal() {
  return animLogo ? animLogo.frames[animLogo.idxFinal].acum : T.total - 400
}

/* Dibuja SOLO la zona animada sobre una base ya pintada */
function dibujarCuadro(ctx, t) {
  ctx.save()
  ctx.beginPath()
  ctx.rect(REG.x, REG.y, REG.w, REG.h)
  ctx.clip()
  ctx.drawImage(baseCv, REG.x, REG.y, REG.w, REG.h, REG.x, REG.y, REG.w, REG.h)

  if (animLogo) {
    /* El GIF del cliente ya trae la secuencia completa */
    dibujarAnim(ctx, t)
    ctx.restore()
    return
  }

  const aFrase = 1 - tramo(t, T.salida[0], T.salida[1])
  if (aFrase > 0) dibujarFrase(ctx, t, aFrase)

  if (t >= T.logo) {
    const avance = clamp01((t - T.logo) / T.trazo)
    const aLogo = Math.min(tramo(t, T.logo, T.logo + 200), 1 - tramo(t, T.cierre[0], T.cierre[1]))
    if (aLogo > 0) dibujarLogoBarrido(ctx, avance, aLogo)
  }
  ctx.restore()
}

/* ════════════════ CODIFICADOR GIF ════════════════════════════ */
function medianCut(muestras, maxCol) {
  const cajas = [{ idx: muestras.map((_, i) => i) }]
  const rango = (caja) => {
    const mm = [
      [255, 0],
      [255, 0],
      [255, 0],
    ]
    for (const i of caja.idx)
      for (let c = 0; c < 3; c++) {
        const v = muestras[i][c]
        if (v < mm[c][0]) mm[c][0] = v
        if (v > mm[c][1]) mm[c][1] = v
      }
    caja.mm = mm
    caja.canal = 0
    let mejor = -1
    for (let c = 0; c < 3; c++) {
      const d = mm[c][1] - mm[c][0]
      if (d > mejor) {
        mejor = d
        caja.canal = c
      }
    }
    caja.rango = mejor
    return mejor
  }
  cajas.forEach(rango)
  while (cajas.length < maxCol) {
    cajas.sort((a, b) => b.rango * b.idx.length - a.rango * a.idx.length)
    const c = cajas.shift()
    if (!c || c.idx.length < 2 || c.rango === 0) {
      if (c) cajas.push(c)
      break
    }
    const ch = c.canal
    c.idx.sort((a, b) => muestras[a][ch] - muestras[b][ch])
    const m = c.idx.length >> 1
    const a = { idx: c.idx.slice(0, m) },
      b = { idx: c.idx.slice(m) }
    rango(a)
    rango(b)
    cajas.push(a, b)
  }
  return cajas.map((c) => {
    let r = 0,
      g = 0,
      b = 0
    for (const i of c.idx) {
      r += muestras[i][0]
      g += muestras[i][1]
      b += muestras[i][2]
    }
    const n = c.idx.length || 1
    return [Math.round(r / n), Math.round(g / n), Math.round(b / n)]
  })
}

function creaMapeador(paleta) {
  const cache = new Map()
  return (r, g, b) => {
    const k = (r << 16) | (g << 8) | b
    const hit = cache.get(k)
    if (hit !== undefined) return hit
    let mejor = 0,
      dist = Infinity
    for (let i = 0; i < paleta.length; i++) {
      const p = paleta[i],
        dr = r - p[0],
        dg = g - p[1],
        db = b - p[2]
      const d = dr * dr * 0.3 + dg * dg * 0.59 + db * db * 0.11
      if (d < dist) {
        dist = d
        mejor = i
      }
    }
    cache.set(k, mejor)
    return mejor
  }
}

function lzw(minCode, indices) {
  const out = []
  let cur = 0,
    bits = 0
  const clear = 1 << minCode,
    eoi = clear + 1
  let codeSize = minCode + 1,
    next = eoi + 1,
    dict = new Map()
  const emit = (code) => {
    cur |= code << bits
    bits += codeSize
    while (bits >= 8) {
      out.push(cur & 255)
      cur >>= 8
      bits -= 8
    }
  }
  emit(clear)
  let prefijo = indices[0]
  for (let i = 1; i < indices.length; i++) {
    const k = indices[i],
      key = prefijo * 4096 + k,
      hit = dict.get(key)
    if (hit !== undefined) {
      prefijo = hit
      continue
    }
    emit(prefijo)
    if (next < 4096) {
      dict.set(key, next)
      next++
      if (next > 1 << codeSize && codeSize < 12) codeSize++
    } else {
      emit(clear)
      dict = new Map()
      next = eoi + 1
      codeSize = minCode + 1
    }
    prefijo = k
  }
  emit(prefijo)
  emit(eoi)
  if (bits > 0) out.push(cur & 255)
  return out
}

function bloques(bytes) {
  const out = []
  for (let i = 0; i < bytes.length; i += 255) {
    const trozo = bytes.slice(i, i + 255)
    out.push(trozo.length, ...trozo)
  }
  out.push(0)
  return out
}

function armaGIF(anchoG, altoG, paleta, cuadros, retardo) {
  let bufer = new Uint8Array(1 << 20),
    n = 0
  const push = (...v) => {
    if (n + v.length > bufer.length) {
      let cap = bufer.length
      while (cap < n + v.length) cap *= 2
      const nuevo = new Uint8Array(cap)
      nuevo.set(bufer.subarray(0, n))
      bufer = nuevo
    }
    for (let i = 0; i < v.length; i++) bufer[n++] = v[i]
  }
  const lista = (arr) => {
    for (let i = 0; i < arr.length; i++) push(arr[i])
  }
  const corto = (v) => push(v & 255, (v >> 8) & 255)

  push(0x47, 0x49, 0x46, 0x38, 0x39, 0x61)
  corto(anchoG)
  corto(altoG)
  push(0xf7, 0, 0)
  for (let i = 0; i < 256; i++) {
    const p = paleta[i] || [0, 0, 0]
    push(p[0], p[1], p[2])
  }
  push(0x21, 0xff, 0x0b)
  lista([..."NETSCAPE2.0"].map((c) => c.charCodeAt(0)))
  push(3, 1, 0, 0, 0)
  for (const c of cuadros) {
    push(0x21, 0xf9, 0x04, 0x04)
    corto(retardo)
    push(0, 0)
    push(0x2c)
    corto(c.x)
    corto(c.y)
    corto(c.w)
    corto(c.h)
    push(0)
    push(8)
    lista(bloques(lzw(8, c.idx)))
  }
  push(0x3b)
  return bufer.subarray(0, n)
}

/* ════════════════ API PÚBLICA ════════════════════════════════ */

async function preparar() {
  if (listo) return

  try {
    await Promise.all([
      document.fonts.load("400 16px Montserrat"),
      document.fonts.load("500 16px Montserrat"),
      document.fonts.load("600 16px Montserrat"),
      document.fonts.load("700 16px Montserrat"),
      document.fonts.load("600 47px Montserrat"),
      document.fonts.load("700 24px Montserrat"),
      document.fonts.load("400 38px Sacramento"),
    ])
    await document.fonts.ready
  } catch (_e) {
    /* sin internet: se usan fuentes del sistema */
  }

  formasImg = await svgAImg(FORMAS)

  baseCv = document.createElement("canvas")
  baseCv.width = W
  baseCv.height = H
  swapCv = document.createElement("canvas")
  swapCv.width = W
  swapCv.height = H

  await cargarLogos()
  listo = true
  recalcular()
}

/* ── Animación propia (GIF del cliente) ──────────────────────── */

/* Límites para no agotar memoria: un GIF de 95 fotogramas a 900x390
   ocuparía ~130 MB en mapas de bits. Submuestreamos y reescalamos. */
const MAX_FRAMES = 60
const ANCHO_TRABAJO = 520

/* ── Lector de GIF en JavaScript puro ─────────────────────────────
   Se implementa a mano en vez de usar ImageDecoder (WebCodecs) porque
   ese API no existe en Firefox y en Chrome tiene una caché interna de
   fotogramas que hace que decode() deje de resolver a mitad de un GIF
   largo. Esto es determinista y funciona en cualquier navegador.      */

/* Descomprime los datos LZW de una imagen del GIF a índices de paleta */
function lzwGif(bytes, minCode, npx) {
  const salida = new Uint8Array(npx)
  const limpiar = 1 << minCode,
    fin = limpiar + 1

  // Diccionario como cadenas prefijo/sufijo (evita arrays anidados)
  const pref = new Int32Array(4096)
  const suf = new Uint8Array(4096)
  const pila = new Uint8Array(4096)

  let tam = minCode + 1,
    sig = fin + 1,
    ant = -1
  let bit = 0,
    cur = 0,
    pos = 0,
    out = 0,
    sp = 0

  while (out < npx) {
    // Rellena el acumulador de bits
    while (bit < tam) {
      if (pos >= bytes.length) return salida
      cur |= bytes[pos++] << bit
      bit += 8
    }
    const code = cur & ((1 << tam) - 1)
    cur >>= tam
    bit -= tam

    if (code === limpiar) {
      tam = minCode + 1
      sig = fin + 1
      ant = -1
      continue
    }
    if (code === fin) break

    let c = code
    if (c >= sig) {
      // caso KwKwK
      if (ant < 0) break
      pila[sp++] = suf[ant]
      c = ant
    }
    while (c >= limpiar) {
      pila[sp++] = suf[c]
      c = pref[c]
    }
    pila[sp++] = c

    while (sp > 0 && out < npx) salida[out++] = pila[--sp]
    sp = 0

    if (ant >= 0 && sig < 4096) {
      pref[sig] = ant
      suf[sig] = c
      sig++
      if ((sig & (sig - 1)) === 0 && tam < 12) tam++ // sig es potencia de 2
    }
    ant = code
  }
  return salida
}

/* Analiza el archivo y devuelve los fotogramas ya compuestos */
function leerGif(buffer) {
  const b = new Uint8Array(buffer)
  const firma = String.fromCharCode(b[0], b[1], b[2], b[3], b[4], b[5])
  if (firma !== "GIF87a" && firma !== "GIF89a") throw new Error("No es un GIF")

  const W0 = b[6] | (b[7] << 8),
    H0 = b[8] | (b[9] << 8)
  const flags = b[10]
  let p = 13

  let gct = null
  if (flags & 0x80) {
    const n = 2 << (flags & 7)
    gct = b.subarray(p, p + n * 3)
    p += n * 3
  }

  // Lienzo acumulado: el GIF compone fotograma sobre fotograma
  const lienzo = document.createElement("canvas")
  lienzo.width = W0
  lienzo.height = H0
  const lctx = lienzo.getContext("2d", { willReadFrequently: true })

  const frames = []
  let delay = 100,
    transp = -1,
    disposal = 0,
    previo = null

  const saltarBloques = () => {
    while (p < b.length && b[p] !== 0) p += b[p] + 1
    p++
  }

  while (p < b.length) {
    const marca = b[p++]

    if (marca === 0x3b) break // fin del archivo

    if (marca === 0x21) {
      // extensión
      const tipo = b[p++]
      if (tipo === 0xf9) {
        // control gráfico
        const len = b[p++]
        const pk = b[p]
        disposal = (pk >> 2) & 7
        transp = pk & 1 ? b[p + 3] : -1
        delay = (b[p + 1] | (b[p + 2] << 8) || 10) * 10 // centésimas → ms
        p += len
        p++ // salta el terminador
      } else {
        saltarBloques()
      }
      continue
    }

    if (marca !== 0x2c) {
      continue
    } // desconocido: ignora

    // Descriptor de imagen
    const ix = b[p] | (b[p + 1] << 8),
      iy = b[p + 2] | (b[p + 3] << 8)
    const iw = b[p + 4] | (b[p + 5] << 8),
      ih = b[p + 6] | (b[p + 7] << 8)
    const ipk = b[p + 8]
    p += 9

    let paleta = gct
    if (ipk & 0x80) {
      // paleta local
      const n = 2 << (ipk & 7)
      paleta = b.subarray(p, p + n * 3)
      p += n * 3
    }
    const entrelazado = !!(ipk & 0x40)

    // Datos comprimidos (sub-bloques)
    const minCode = b[p++]
    const trozos = []
    let total = 0
    while (p < b.length && b[p] !== 0) {
      const n = b[p++]
      trozos.push(b.subarray(p, p + n))
      total += n
      p += n
    }
    p++ // terminador
    const datos = new Uint8Array(total)
    let o = 0
    for (const t of trozos) {
      datos.set(t, o)
      o += t.length
    }

    const idx = lzwGif(datos, minCode, iw * ih)

    // Guarda el estado si hay que restaurarlo después
    if (disposal === 3) previo = lctx.getImageData(0, 0, W0, H0)

    // Pinta el trozo sobre el lienzo acumulado
    const img = lctx.createImageData(iw, ih)
    const px = img.data
    for (let i = 0; i < idx.length; i++) {
      const c = idx[i]
      if (c === transp) {
        px[i * 4 + 3] = 0
        continue
      }
      const q = c * 3
      px[i * 4] = paleta[q]
      px[i * 4 + 1] = paleta[q + 1]
      px[i * 4 + 2] = paleta[q + 2]
      px[i * 4 + 3] = 255
    }

    if (entrelazado) {
      // Reordena las filas del formato entrelazado
      const orden = new ImageData(iw, ih)
      const pasos = [
        [0, 8],
        [4, 8],
        [2, 4],
        [1, 2],
      ]
      let fila = 0
      for (const [ini, inc] of pasos) {
        for (let y = ini; y < ih; y += inc, fila++) {
          orden.data.set(px.subarray(fila * iw * 4, (fila + 1) * iw * 4), y * iw * 4)
        }
      }
      px.set(orden.data)
    }

    // putImageData ignora la transparencia, así que se compone aparte
    const trozo = document.createElement("canvas")
    trozo.width = iw
    trozo.height = ih
    trozo.getContext("2d").putImageData(img, 0, 0)
    lctx.drawImage(trozo, ix, iy)

    // Copia del lienzo completo = fotograma visible
    const copia = document.createElement("canvas")
    copia.width = W0
    copia.height = H0
    copia.getContext("2d").drawImage(lienzo, 0, 0)
    frames.push({ cv: copia, delay })

    // Aplica el método de descarte para el siguiente
    if (disposal === 2) lctx.clearRect(ix, iy, iw, ih)
    else if (disposal === 3 && previo) lctx.putImageData(previo, 0, 0)

    transp = -1
  }

  return { frames, w: W0, h: H0 }
}

/* Decodifica el GIF, lo reescala y calcula encuadre y fotograma final */
async function decodificarGif(ruta) {
  const resp = await fetch(ruta, { cache: "no-cache" })
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
  const crudo = leerGif(await resp.arrayBuffer())
  if (crudo.frames.length < 2) return null // no es animado

  const n = crudo.frames.length
  const paso = Math.max(1, Math.ceil(n / MAX_FRAMES))

  const k = Math.min(1, ANCHO_TRABAJO / crudo.w)
  const tw = Math.max(1, Math.round(crudo.w * k))
  const th = Math.max(1, Math.round(crudo.h * k))

  const med = document.createElement("canvas")
  med.width = tw
  med.height = th
  const mctx = med.getContext("2d", { willReadFrequently: true })

  const frames = []
  let acum = 0,
    x0 = tw,
    y0 = th,
    x1 = 0,
    y1 = 0

  for (let i = 0; i < n; i += paso) {
    // Conserva la duración original sumando los fotogramas omitidos
    let delay = 0
    for (let j = i; j < Math.min(n, i + paso); j++) delay += crudo.frames[j].delay
    delay = Math.max(20, delay)

    const cv = document.createElement("canvas")
    cv.width = tw
    cv.height = th
    const cx = cv.getContext("2d")
    cx.imageSmoothingQuality = "high"
    cx.drawImage(crudo.frames[i].cv, 0, 0, tw, th)

    mctx.clearRect(0, 0, tw, th)
    mctx.drawImage(cv, 0, 0)
    const d = mctx.getImageData(0, 0, tw, th).data

    /* Umbral RELATIVO al contraste del propio fotograma. Con un umbral
       fijo de blanco, durante un desvanecido sólo se detectaba el
       núcleo más oscuro del logo: el recuadro se encogía y el zoom se
       disparaba. Midiendo respecto al píxel más oscuro de cada
       fotograma, un logo pálido se mide igual que uno sólido. */
    let minLum = 255
    for (let py = 0; py < th; py += 2) {
      for (let px = 0; px < tw; px += 2) {
        const o = (py * tw + px) * 4
        if (d[o + 3] < 16) continue
        const l = Math.min(d[o], d[o + 1], d[o + 2])
        if (l < minLum) minLum = l
      }
    }
    const lim = 255 - (255 - minLum) * 0.05

    // Recuadro de ESTE fotograma (para el zoom que sigue al contenido)
    // y su "tinta": cuánto color sólido tiene, para elegir la imagen fija.
    let cuenta = 0,
      tinta = 0,
      bx0 = tw,
      by0 = th,
      bx1 = 0,
      by1 = 0
    for (let py = 0; py < th; py += 2) {
      for (let px = 0; px < tw; px += 2) {
        const o = (py * tw + px) * 4
        if (d[o + 3] < 16) continue // transparente
        const l = Math.min(d[o], d[o + 1], d[o + 2])
        if (l >= lim || l > 250) continue // fondo
        cuenta++
        tinta += 255 - l
        if (px < bx0) bx0 = px
        if (px > bx1) bx1 = px
        if (py < by0) by0 = py
        if (py > by1) by1 = py
      }
    }
    if (cuenta > 30) {
      if (bx0 < x0) x0 = bx0
      if (bx1 > x1) x1 = bx1
      if (by0 < y0) y0 = by0
      if (by1 > y1) y1 = by1
    }

    frames.push({
      img: cv,
      delay,
      acum,
      tinta,
      bb: cuenta > 30 ? { x0: bx0, y0: by0, x1: bx1, y1: by1 } : null,
    })
    acum += delay
  }

  let rec
  if (x1 <= x0 || y1 <= y0) {
    rec = { x: 0, y: 0, w: tw, h: th }
  } else {
    const m = 6
    const rx = Math.max(0, x0 - m),
      ry = Math.max(0, y0 - m)
    rec = {
      x: rx,
      y: ry,
      w: Math.min(tw - rx, x1 - rx + 1 + m),
      h: Math.min(th - ry, y1 - ry + 1 + m),
    }
  }

  const tMax = frames.reduce((m, f) => Math.max(m, f.tinta), 0)

  /* En los desvanecidos el recuadro se encoge hacia el núcleo más
     oscuro (los píxeles pálidos ya no cuentan como contenido) y el
     zoom se disparaba, cortando el logo. Sólo los fotogramas bien
     sólidos definen encuadre; el resto hereda del vecino y la
     transición la resuelve el suavizado. */
  const umbral = tMax * 0.12
  for (const f of frames) if (f.tinta < umbral) f.bb = null

  /* Imagen fija de la firma: el fotograma más sólido de la segunda
     mitad. Buscar "el último con contenido" fallaba, porque muchas
     animaciones terminan con un desvanecido y salía el logo pálido. */
  let idxFinal = frames.length - 1,
    mejor = -1
  for (let i = Math.floor(frames.length / 2); i < frames.length; i++) {
    if (frames[i].tinta > mejor) {
      mejor = frames[i].tinta
      idxFinal = i
    }
  }

  calcularVistas(frames, tw, th, rec)
  return { frames, total: acum, w: tw, h: th, rec, idxFinal }
}

/* ── Zoom automático suave ───────────────────────────────────────
   Cada fotograma tiene su propio encuadre, pero NO se usa tal cual:
   se toma la UNIÓN de los recuadros de una ventana de fotogramas
   alrededor. Eso hace dos cosas importantes:
     · Anticipa. Mientras un texto se escribe letra a letra, el
       encuadre ya tiene el tamaño que tendrá al terminar, así que
       nunca se corta ni «respira».
     · Estabiliza. Dentro de una misma escena el encuadre es constante
       y sólo cambia al pasar de una escena a otra.                    */
function calcularVistas(frames, tw, th, global) {
  const MARGEN = 1.18 // aire alrededor del contenido
  const N = frames.length

  const respaldo = {
    x0: global.x,
    y0: global.y,
    x1: global.x + global.w - 1,
    y1: global.y + global.h - 1,
  }

  /* Segmentación en ESCENAS. Los fotogramas sin contenido (los que
     separan una escena de otra) hacen de corte. El encuadre es
     CONSTANTE dentro de cada escena y vale la unión de todo lo que
     esa escena llega a mostrar.

     Esto es lo que hace que el logo se vea grande desde el primer
     fotograma en que aparece: antes se usaba una ventana centrada en
     cada fotograma, que arrastraba el encuadre ancho de la escena
     anterior y sólo se cerraba —de golpe— al final.                 */
  const escena = new Array(N).fill(null)
  let i = 0
  while (i < N) {
    if (!frames[i].bb) {
      i++
      continue
    }
    let j = i
    let x0 = Infinity,
      y0 = Infinity,
      x1 = -Infinity,
      y1 = -Infinity
    while (j < N && frames[j].bb) {
      const b = frames[j].bb
      if (b.x0 < x0) x0 = b.x0
      if (b.y0 < y0) y0 = b.y0
      if (b.x1 > x1) x1 = b.x1
      if (b.y1 > y1) y1 = b.y1
      j++
    }
    const uni = { x0, y0, x1, y1 }
    for (let k = i; k < j; k++) escena[k] = uni
    i = j
  }

  /* El cambio de encuadre se reparte a lo largo del corte, MIENTRAS
     no se ve nada. Así la escena siguiente entra ya con su tamaño
     definitivo y no se percibe un salto al aparecer el logo.
     El crecimiento se completa al 75% del hueco, para que el logo
     empiece a desvanecerse hacia dentro estando ya grande. */
  const suave = (u) => u * u * (3 - 2 * u)
  const entre = (A, B, u) => ({
    x0: A.x0 + (B.x0 - A.x0) * u,
    y0: A.y0 + (B.y0 - A.y0) * u,
    x1: A.x1 + (B.x1 - A.x1) * u,
    y1: A.y1 + (B.y1 - A.y1) * u,
  })

  let k = 0
  while (k < N) {
    if (escena[k]) {
      k++
      continue
    }
    const a = k
    while (k < N && !escena[k]) k++
    const b = k - 1 // hueco [a..b]
    const P = a > 0 ? escena[a - 1] : null // escena que termina
    const S = k < N ? escena[k] : null // escena que empieza
    const largo = b - a + 1
    for (let j = a; j <= b; j++) {
      if (P && S) {
        const u = Math.min(1, (j - a + 1) / largo / 0.75)
        escena[j] = entre(P, S, suave(u))
      } else {
        escena[j] = P || S || respaldo
      }
    }
  }

  const bbDe = (k) => frames[k].bb || escena[k] || respaldo

  for (let i = 0; i < N; i++) {
    const e = escena[i] || respaldo
    let x0 = e.x0,
      y0 = e.y0,
      x1 = e.x1,
      y1 = e.y1

    // Garantía anti-recorte: el encuadre siempre contiene lo que se ve
    const p = bbDe(i)
    x0 = Math.min(x0, p.x0)
    y0 = Math.min(y0, p.y0)
    x1 = Math.max(x1, p.x1)
    y1 = Math.max(y1, p.y1)

    /* El recuadro conserva la proporción del CONTENIDO, no la de la
       zona destino. Forzar aquí la proporción del destino deformaba
       la marca cuando el contenido era más ancho que alto: al recortar
       contra el borde del fotograma se perdía la relación de aspecto.
       El ajuste a la zona se hace al dibujar, con «contain». */
    let w = (x1 - x0 + 1) * MARGEN
    let h = (y1 - y0 + 1) * MARGEN
    let cx = (x0 + x1) / 2,
      cy = (y0 + y1) / 2

    // Reduce sin deformar si el recuadro no cabe en el fotograma
    const ajuste = Math.min(1, tw / w, th / h)
    w *= ajuste
    h *= ajuste

    cx = Math.min(tw - w / 2, Math.max(w / 2, cx))
    cy = Math.min(th - h / 2, Math.max(h / 2, cy))

    frames[i].vista = { cx, cy, w, h }
  }
  for (const f of frames) delete f.bb
}

/* Fotograma que corresponde al instante t (ms) del bucle */
function frameEn(t) {
  const a = animLogo
  const tt = ((t % a.total) + a.total) % a.total
  let lo = 0,
    hi = a.frames.length - 1
  while (lo < hi) {
    const m = (lo + hi + 1) >> 1
    if (a.frames[m].acum <= tt) lo = m
    else hi = m - 1
  }
  return a.frames[lo]
}

/* Dibuja el GIF del cliente con su encuadre suavizado.
   Se ajusta con «contain» para que NUNCA se deforme: la proporción
   original del logo se respeta siempre, quede el aire donde quede.
   El control de tamaño acerca (>100%) o aleja (<100%) la vista. */
function dibujarAnim(ctx, t) {
  const f = frameEn(t)
  const v = f.vista
  const k = 100 / escalaLogo

  const sw = v.w * k,
    sh = v.h * k
  const sx = v.cx - sw / 2,
    sy = v.cy - sh / 2

  const esc = Math.min(REG.w / sw, REG.h / sh)
  const dw = sw * esc,
    dh = sh * esc
  const dx = REG.x + (REG.w - dw) / 2
  const dy = REG.y + (REG.h - dh) / 2

  ctx.drawImage(f.img, sx, sy, sw, sh, dx, dy, dw, dh)
}

/* Carga logo y marca; si el logo es un GIF animado, lo decodifica */
async function cargarLogos() {
  const rutaLogo = empresa.emp_logo || ""
  const ruta25 = empresa.emp_logo25 || ""

  animLogo = null

  if (rutaLogo) {
    // ¿GIF animado? Entonces manda él y no hace falta nada más.
    if (/\.gif(\?|$)/i.test(rutaLogo)) {
      try {
        animLogo = await decodificarGif(rutaLogo)
      } catch (e) {
        console.warn("[firma] no se pudo decodificar el GIF:", e.message)
      }
    }
    try {
      const im = await cargarImg(rutaLogo)
      logoFull = { el: im, w: im.naturalWidth, h: im.naturalHeight }
    } catch {
      logoFull = crearLogoFallback()
    }
  } else logoFull = crearLogoFallback()

  if (ruta25) {
    try {
      const im = await cargarImg(ruta25)
      marca25 = { el: im, w: im.naturalWidth, h: im.naturalHeight }
    } catch {
      marca25 = crearMarca25Fallback()
    }
  } else marca25 = crearMarca25Fallback()
}

/* Recalcula métricas de texto y repinta la base */
function recalcular() {
  if (!listo) return
  const ctx = baseCv.getContext("2d")
  anchoFrase = anchoTexto(ctx, FRASE, FS_FRASE, 400)
  fsPalabra = (47 * anchoFrase) / anchoTexto(ctx, PALABRA, 47, 600)
  dibujarBase(ctx)
}

/* Renderiza el estado final (logo completo visible) en un canvas */
function pintarFinal(ctx) {
  ctx.drawImage(baseCv, 0, 0)
  dibujarCuadro(ctx, tFinal())
}

let animId = 0
function reproducir(canvas) {
  cancelAnimationFrame(animId)
  if (!listo || !canvas) return
  const ctx = canvas.getContext("2d")
  const t0 = performance.now()
  const dur = duracion()
  const paso = () => {
    const t = (performance.now() - t0) % dur
    ctx.drawImage(baseCv, 0, 0)
    dibujarCuadro(ctx, t)
    animId = requestAnimationFrame(paso)
  }
  paso()
}

function detener(canvas) {
  cancelAnimationFrame(animId)
  animId = 0
  if (listo && canvas) pintarFinal(canvas.getContext("2d"))
}

/* ── Exportar PNG ────────────────────────────────────────────── */
function png(escala) {
  escala = escala || 1
  const c = document.createElement("canvas")
  c.width = Math.round((W * escala) / 2)
  c.height = Math.round((H * escala) / 2)

  const tmp = document.createElement("canvas")
  tmp.width = W
  tmp.height = H
  pintarFinal(tmp.getContext("2d"))

  const cc = c.getContext("2d")
  cc.imageSmoothingQuality = "high"
  cc.drawImage(tmp, 0, 0, c.width, c.height)
  return new Promise((r) => c.toBlob(r, "image/png"))
}

/* ── Exportar GIF animado ────────────────────────────────────── */
async function gif(opts) {
  opts = opts || {}
  const FPS = Math.min(25, Math.max(5, opts.fps || 12))
  const ESCALA = Math.min(2, Math.max(0.5, opts.escala || 1))
  const onProg = opts.onProgreso

  const retardo = Math.round(100 / FPS)
  const dt = retardo * 10
  const n = Math.max(2, Math.round(duracion() / dt))

  const gw = Math.round((W * ESCALA) / 2),
    gh = Math.round((H * ESCALA) / 2)
  const k = gw / W

  const sal = document.createElement("canvas")
  sal.width = gw
  sal.height = gh
  const sc = sal.getContext("2d")
  sc.imageSmoothingQuality = "high"

  const maestro = document.createElement("canvas")
  maestro.width = W
  maestro.height = H
  const mc = maestro.getContext("2d")

  const reg = {
    x: Math.floor(REG.x * k),
    y: Math.floor(REG.y * k),
    w: Math.ceil(REG.w * k) + 1,
    h: Math.ceil(REG.h * k) + 1,
  }
  reg.w = Math.min(reg.w, gw - reg.x)
  reg.h = Math.min(reg.h, gh - reg.y)

  const cuadros = [],
    muestras = []
  for (let i = 0; i < n; i++) {
    const t = i * dt
    mc.drawImage(baseCv, 0, 0)
    dibujarCuadro(mc, t)
    sc.clearRect(0, 0, gw, gh)
    sc.drawImage(maestro, 0, 0, gw, gh)

    const zona = i === 0 ? { x: 0, y: 0, w: gw, h: gh } : reg
    const px = sc.getImageData(zona.x, zona.y, zona.w, zona.h).data
    cuadros.push({ x: zona.x, y: zona.y, w: zona.w, h: zona.h, px })

    if (i % 8 === 0)
      for (let p = 0; p < px.length; p += 4 * 7) muestras.push([px[p], px[p + 1], px[p + 2]])
    if (onProg && i % 6 === 0) {
      onProg((0.6 * i) / n)
      await esperar()
    }
  }

  const paleta = medianCut(muestras, 256)
  const mapear = creaMapeador(paleta)

  for (let i = 0; i < cuadros.length; i++) {
    const c = cuadros[i],
      px = c.px,
      idx = new Uint8Array(px.length / 4)
    for (let p = 0, q = 0; p < px.length; p += 4, q++) idx[q] = mapear(px[p], px[p + 1], px[p + 2])
    c.idx = idx
    delete c.px
    if (onProg && i % 6 === 0) {
      onProg(0.6 + (0.35 * i) / cuadros.length)
      await esperar()
    }
  }

  const bytes = armaGIF(gw, gh, paleta, cuadros, retardo)
  if (onProg) onProg(1)
  return new Blob([bytes], { type: "image/gif" })
}

/* ── Interfaz del módulo ─────────────────────────────────────── */
export const FIRMA = {
  preparar,
  get listo() {
    return listo
  },

  /* Cambia los datos del trabajador y repinta */
  setTrabajador(t) {
    datos = {
      nombre: t?.nombre || "Nombre Apellido",
      cargo: t?.cargo || "Cargo / Posición",
      telefono: t?.telefono || "",
      email: t?.email || "nombre.apellido@setieca.com.ve",
    }
    recalcular()
  },

  /* Datos core de la compañía. Recarga los logos si cambió su ruta;
     si se está usando el respaldo vectorial lo regenera siempre,
     porque incluye el RIF y la razón social (que sí son editables). */
  async setEmpresa(e) {
    const antesLogo = empresa.emp_logo,
      antes25 = empresa.emp_logo25
    empresa = e || {}

    const esc = parseFloat(empresa.emp_logo_escala)
    escalaLogo = Number.isFinite(esc) && esc >= 20 && esc <= 260 ? esc : 100

    if (listo) {
      const rutaCambio = empresa.emp_logo !== antesLogo || empresa.emp_logo25 !== antes25
      if (rutaCambio) await cargarLogos()
      else if (!empresa.emp_logo) logoFull = crearLogoFallback()
    }
    recalcular()
  },

  /* Ajuste en vivo del tamaño del logo, sin recargar nada */
  setEscalaLogo(pct) {
    const v = parseFloat(pct)
    if (Number.isFinite(v) && v >= 20 && v <= 260) escalaLogo = v
  },

  /* Qué se está usando ahora: sirve para informar en la interfaz */
  get info() {
    return {
      animado: !!animLogo,
      frames: animLogo ? animLogo.frames.length : 0,
      duracion: duracion(),
      escala: escalaLogo,
      fuente: animLogo ? "gif" : empresa.emp_logo ? "imagen" : "vectorial",
    }
  },

  get datos() {
    return Object.assign({}, datos)
  },
  get empresa() {
    return Object.assign({}, empresa)
  },

  pintarFinal(canvas) {
    if (listo && canvas) pintarFinal(canvas.getContext("2d"))
  },

  /* Pinta un instante concreto de la animación (0…DURACION). Útil para depurar. */
  pintarEn(canvas, t) {
    if (!listo || !canvas) return
    const ctx = canvas.getContext("2d")
    ctx.drawImage(baseCv, 0, 0)
    dibujarCuadro(ctx, t)
  },

  reproducir,
  detener,
  get DURACION() {
    return duracion()
  },
  png,
  gif,
  ANCHO: W,
  ALTO: H,
}
